import { Spacing } from '@persona-id/design-tokens';
import { compact } from 'lodash/fp';
import { ChangeEvent, useMemo, useState } from 'react';
import { StylesConfig } from 'react-select';
import styled from 'styled-components';

import { LOGIC_RULE_INPUT_ID } from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateScreenComponentEditor/ComponentEditorSections/ComponentJsonLogicEditor/blocks/FieldOrValueInput';
import ReactSelect, { OptionType, ReactCreatableSelect } from 'dashboard/components/ReactSelect';
import { DateInput } from 'dashboard/components/WorkflowControlPanel/StepConfigConditional/BaseConditionalInputPossibilities';
import { getJsonSchemaType, JsonLogicValue } from 'dashboard/lib/workflows';
import { generateConditionalOptions } from 'dashboard/lib/workflows/conditional';
import Input from 'design-system/components/Input';
import { paragraphMixins } from 'design-system/mixins/text';
import { JsonSchemaAllTypes, JsonSchemaCustomTypes } from 'lib/fields';
import { JsonSchema, JsonSchemaTypes } from 'lib/json-schema';
import { assertNever } from 'lib/utils/types';

const StyledInput = styled(Input)`
  height: ${({ theme }) => theme.element.height.xs};
  ${paragraphMixins.md};
`;

const styles: StylesConfig = {
  control: () => ({
    height: 'auto',
  }),
  valueContainer: () => ({
    height: 'auto',
    minHeight: '30px',
    maxHeight: '80px',
    overflowY: 'auto',
    paddingTop: 0,
    paddingBottom: 0,
  }),
  multiValueLabel: () => ({
    padding: '2px',
  }),
  option: () => ({
    paddingTop: `${Spacing[2]}`,
    paddingBottom: `${Spacing[2]}`,
  }),
  indicatorsContainer: (_, state) =>
    state.isMulti
      ? {
          display: 'none',
        }
      : {},
};

const valueToOptions = (value: JsonLogicValue): OptionType<string>[] => {
  if (Array.isArray(value)) {
    return value.map((val) => {
      return {
        value: val,
        label: val,
      };
    });
  } else {
    value = value || '';
    return [
      {
        value: value.toString(),
        label: value.toString(),
      },
    ];
  }
};

interface ValueInputProps {
  formattedValue: JsonLogicValue;
  fieldSchema: JsonSchema | null;
  isMulti: boolean;
  disabled: boolean;
  hasError: boolean;
  onChange: (value: JsonLogicValue) => void;
}

const ValueInput = ({
  formattedValue,
  fieldSchema,
  isMulti,
  disabled,
  hasError,
  onChange,
}: ValueInputProps) => {
  const [inputStateValue, setInputStateValue] = useState<string>((formattedValue as string) || '');
  const { schemaType, options } = useMemo<{
    schemaType: JsonSchemaTypes | JsonSchemaCustomTypes;
    options: { label: string; value: string }[];
  }>(
    () =>
      fieldSchema != null
        ? {
            schemaType: getJsonSchemaType(fieldSchema),
            options: generateConditionalOptions(fieldSchema),
          }
        : { schemaType: JsonSchemaAllTypes.Unknown, options: [] },
    [fieldSchema],
  );

  const onChangeInput = (e: ChangeEvent<HTMLInputElement>) => {
    e.stopPropagation();
    e.preventDefault();
    setInputStateValue(e.target.value);
  };

  const onBlurInput = () => {
    onChange(inputStateValue);
  };

  switch (schemaType) {
    case JsonSchemaAllTypes.Enum:
    case JsonSchemaAllTypes.GroupedEnum:
    case JsonSchemaAllTypes.MultiEnum:
      return (
        <ReactSelect
          id={`${LOGIC_RULE_INPUT_ID}__enum`}
          aria-label='Select option'
          menuPlacement='bottom'
          styles={styles as any}
          value={formattedValue as string | string[]}
          options={options}
          disabled={disabled}
          hasError={hasError}
          hideSelectedOptions={false}
          isMulti={isMulti}
          onChange={onChange}
          isSearchable
          portal
        />
      );
    case JsonSchemaAllTypes.String:
      if (isMulti) {
        return (
          <ReactCreatableSelect
            id={`${LOGIC_RULE_INPUT_ID}__multi-string`}
            menuPlacement='bottom'
            styles={styles as any}
            value={formattedValue as string[]}
            options={valueToOptions(formattedValue)}
            disabled={disabled}
            hasError={hasError}
            hideSelectedOptions={false}
            onChange={(value: string[]) => onChange(compact(value))}
            isSearchable
            isMulti
            portal
          />
        );
      } else {
        return (
          <StyledInput
            id={`${LOGIC_RULE_INPUT_ID}__single-string`}
            aria-label='Enter text value'
            value={inputStateValue}
            disabled={disabled}
            hasError={hasError}
            onBlur={onBlurInput}
            onChange={onChangeInput}
          />
        );
      }
    case JsonSchemaAllTypes.Integer:
    case JsonSchemaAllTypes.Number:
      return (
        <StyledInput
          type='number'
          id={`${LOGIC_RULE_INPUT_ID}__number`}
          aria-label='Input number value'
          value={inputStateValue}
          disabled={disabled}
          hasError={hasError}
          onBlur={onBlurInput}
          onChange={onChangeInput}
        />
      );
    case JsonSchemaAllTypes.Boolean: {
      const options: OptionType<boolean>[] = [
        {
          label: 'true',
          value: true,
        },
        {
          label: 'false',
          value: false,
        },
      ];
      let value = formattedValue;
      // existing server-side logic rules may have boolean values as strings,
      // normalize to boolean to display correctly within rule select.
      if (typeof formattedValue === 'string') {
        if (formattedValue.toLowerCase() === 'true') {
          value = true;
        } else if (formattedValue.toLowerCase() === 'false') {
          value = false;
        }
      }
      return (
        <ReactSelect
          id={`${LOGIC_RULE_INPUT_ID}__boolean`}
          aria-label='Select true/false'
          menuPlacement='bottom'
          styles={styles as any}
          value={value as any}
          options={options as any}
          hideSelectedOptions={false}
          onChange={onChange}
        />
      );
    }
    case JsonSchemaAllTypes.Date:
      return <DateInput onChange={onChange} value={formattedValue} />;
    case JsonSchemaAllTypes.Array:
    case JsonSchemaAllTypes.File:
    case JsonSchemaAllTypes.Object:
    case JsonSchemaAllTypes.Relation:
    case JsonSchemaAllTypes.Unknown:
      return null;
    default:
      assertNever(schemaType);
  }
};

export default ValueInput;
